












































































import {computed, defineComponent, PropType, reactive, ref} from "@vue/composition-api";
import AppLayout from "@tool/Layouts/AppLayout.vue";
import Breadcrumbs from "@tool/Components/Breadcrumbs.vue";
import InputErrors from "@tool/Components/InputErrors.vue";
import {DemoTemplates} from "@tool/Builder/helpers/BuilderDemoData";
import ThemeBlock from "@base/Components/theme/ThemeBlock.vue";
import BuilderSettingsForm from "@tool/Components/BuilderSettingsForm.vue";
import AddParameterInput from "@tool/Pages/App/Teams/Projects/Create/AddParameterInput.vue";
import VueScrollTo from "vue-scrollto";
import {useInertia} from "@tool/Helpers/useInertia";
// @ts-ignore
import {useToast} from "vue-toastification/composition";
import FormError from "@tool/Components/FormError.vue";
import {ParameterType, ProjectEntityInterface, TeamEntityInterface} from "@tool/tool.types";
import useFormHelper from "@tool/Helpers/useFormHelper";
import useAxios from "@tool/Helpers/useAxios";
import useBuilderStore from "@tool/store/useBuilderStore";
import RequiresUpgrade from "@tool/Components/RequiresUpgrade.vue";
import useSwal from "@tool/Helpers/useSwal";

interface SlimParameterInterface {
    name: string,
    type: ParameterType
}

interface SelectedParameterInterface {
    type: 'template' | 'manual',
    name: string;
}

interface ParametersCollection {
    utm_source: SelectedParameterInterface[];
    utm_medium: SelectedParameterInterface[];
    utm_campaign: SelectedParameterInterface[];
    utm_term: SelectedParameterInterface[];
    utm_content: SelectedParameterInterface[];

    all: SlimParameterInterface[]
}

export default defineComponent({
    components: {
        BuilderSettingsForm,
        Breadcrumbs,
        AppLayout,
        InputErrors,
        ThemeBlock,
        AddParameterInput,
        FormError,
        RequiresUpgrade
    },
    props: {
        canCreateProject: {
            type: Boolean,
            required: true
        },
        team: {
            type: Object as PropType<TeamEntityInterface>,
            required: true
        },
    },
    setup(props) {

        const toast = useToast();

        const tabIndex = ref(0);

        const {projectBuilderSettings} = useBuilderStore();

        const form = useFormHelper({
            name: ''
        });

        /**
         * Templates
         */
        const formTemplates = reactive(DemoTemplates.map((template, index) => {
            return {
                'name': template.name,
                'active': false,
                'parameters': template.parameters.map(parameter => {
                    return {
                        'name': parameter.name,
                        'type': parameter.type
                    }
                })
            }
        }));

        const hasSelectedTemplate = computed<boolean>(() => !!formTemplates.find(template => template.active === true));

        const toggleAllTemplates = (select: boolean) => {
            for (let i = 0; i < formTemplates.length; i++) {
                formTemplates[i].active = select;
            }
        }

        /**
         * Parameters
         */

        const formParametersManual = ref<SlimParameterInterface[]>([]);

        const parameterAdd = (type: ParameterType, name: string) => {
            if (formParametersMerged.value[type].findIndex(mergedParam => mergedParam.name === name) > -1) {
                // Already exists
                return;
            }

            formParametersManual.value.push({
                type,
                name
            });
        }

        const parameterRemove = (type: ParameterType, name: string) => {
            const index = formParametersManual.value.findIndex(parameter => parameter.type === type && parameter.name === name);

            if (index > -1) {
                formParametersManual.value.splice(index, 1);
            }
        }

        const formParametersMerged = computed<ParametersCollection>(() => {
            const parameters: ParametersCollection = {
                utm_source: [],
                utm_medium: [],
                utm_term: [],
                utm_content: [],
                utm_campaign: [],

                all: []
            }

            // Build from templates
            for (let i = 0; i < formTemplates.length; i++) {
                if (formTemplates[i].active === false) {
                    continue;
                }
                for (let j = 0; j < formTemplates[i].parameters.length; j++) {
                    const parameter = formTemplates[i].parameters[j];
                    if (parameters[parameter.type].findIndex(existingParameter => existingParameter.name === parameter.name) === -1) {
                        parameters[parameter.type].push({
                            type: 'template',
                            name: parameter.name
                        });

                        parameters.all.push(parameter);
                    }
                }
            }

            for (let i = 0; i < formParametersManual.value.length; i++) {
                const manualParameter = formParametersManual.value[i];
                if (parameters[manualParameter.type].findIndex(existingParameter => existingParameter.name === manualParameter.name) === -1) {
                    parameters[manualParameter.type].push({
                        type: 'manual',
                        name: manualParameter.name
                    });

                    parameters.all.push(manualParameter);
                }
            }

            return parameters;
        });

        const parameterTypes: {
            name: ParameterType,
            title: string,
            description: string
        }[] = [
            {
                name: 'utm_campaign',
                title: 'Campaigns',
                description: 'Identifies a specific product promotion or strategic campaign.'
            },
            {
                name: 'utm_source',
                title: 'Sources',
                description: 'Identifies which site sent the traffic, and is a required parameter.'
            },
            {
                name: 'utm_medium',
                title: 'Mediums',
                description: 'Identifies what type of link was used, such as cost per click or email.'
            },
            {
                name: 'utm_term',
                title: 'Terms',
                description: 'Identifies search terms.'
            },
            {
                name: 'utm_content',
                title: 'Contents',
                description: 'Identifies what specifically was clicked to bring the user to the site, such as a banner ad or a text link. It is often used for A/B testing and content-targeted ads.'
            }
        ];

        /**
         * Submit
         */
        const onSubmit = async () => {
            if (form.value.data.name === '') {
                alert('Name of the project cannot be empty.');
                return;
            }

            const response = await form.value.submit((data) => {
                return useAxios().post<ProjectEntityInterface>(route('app.team.projects.store', props.team.id), {
                    name: data.name,
                    templates: formTemplates.filter(template => template.active === true),
                    parameters: formParametersMerged.value.all,
                    settings: projectBuilderSettings.value
                });
            });

            if (response.success) {
                useInertia().get(route('app.project.builder.index', response.success.slug), {}, {
                    onFinish() {
                        useSwal().fire({
                            title: 'Project was successfully created!',
                            icon: 'success'
                        });
                    }
                });
            } else {
                toast.error('There was a problem creating new project!');
            }
        }

        /**
         * Misc
         */
        const onTabChanged = () => {
            VueScrollTo.scrollTo("#project-create", 1000, {
                offset: -20
            });
        }

        return {
            tabIndex,

            // Templates
            formTemplates,
            hasSelectedTemplate,
            toggleAllTemplates,

            // Parameters
            parameterTypes,
            formParametersMerged,
            parameterRemove,
            parameterAdd,

            // Name
            form,

            // Submit
            onSubmit,

            // Misc
            onTabChanged,

        }

    }
})
